/** Frame-budget decisions separated from the renderer so cadence edge cases
 *  can be exercised without a browser/WebGL context. */
export function frameHasHeadroom(frameMs: number, busyMs: number, jankCount: number): boolean {
  if (jankCount !== 0) return false;
  // A stable 30 Hz display/browser cadence has a ~33.3 ms frame interval even
  // when rendering takes only a few milliseconds. Treat actual work as the
  // signal there; otherwise the quality ladder eventually halves it to 15 Hz.
  return frameMs < 13 || (frameMs < 40 && busyMs < 9);
}

export function frameIsOverBudget(frameMs: number, busyMs: number, thresholdMs: number): boolean {
  if (frameMs <= thresholdMs) return false;
  // Above ~28 ms, cadence alone cannot distinguish an overloaded 60 Hz panel
  // from a healthy 30 Hz panel. Requiring measured work keeps native/throttled
  // 30 Hz smooth while still relieving a renderer that is actually busy.
  return frameMs < 28 || busyMs > 12;
}

/** Fixed-bucket frame-interval window for the perf HUD: avg / p95 / max /
 *  spike-count over a flush interval, zero steady-state allocation. This is
 *  EVIDENCE, not control logic — the quality ladder keeps using the EMAs
 *  above, which by design barely notice the isolated 50 ms hitches this
 *  window exists to expose. */
export class FrameWindow {
  private count = 0;
  private sum = 0;
  private max = 0;
  private spikes = 0; // frames over 50 ms — the "hitch" the EMA hides
  private readonly buckets = new Uint32Array(64); // 1 ms buckets; last catches 63+

  push(ms: number): void {
    if (!Number.isFinite(ms) || ms < 0) return;
    this.count++;
    this.sum += ms;
    if (ms > this.max) this.max = ms;
    if (ms > 50) this.spikes++;
    this.buckets[Math.min(63, Math.floor(ms))]++;
  }

  /** Upper edge (ms) of the bucket holding the 95th-percentile frame — ≤1 ms
   *  coarse, which is plenty for "p95 33 vs avg 17" style evidence. */
  p95(): number {
    if (this.count === 0) return 0;
    const need = Math.ceil(this.count * 0.95);
    let seen = 0;
    for (let i = 0; i < 64; i++) {
      seen += this.buckets[i];
      if (seen >= need) return i + 1;
    }
    return 64;
  }

  avg(): number { return this.count ? this.sum / this.count : 0; }
  maxMs(): number { return this.max; }
  frames(): number { return this.count; }
  spikeCount(): number { return this.spikes; }

  reset(): void {
    this.count = 0;
    this.sum = 0;
    this.max = 0;
    this.spikes = 0;
    this.buckets.fill(0);
  }
}
